import { Button, ConfirmDialog, DataTable, fmt, PageHead, Switch, useToast, type Column } from "@nabble/ui";
import { useCallback, useEffect, useMemo, useState } from "react";
import { errorMessage } from "../../../errors";
import { fetchModules, setModuleEnabled, type AdminModule } from "../../../api/adminModules";

export default function AdminModulesPage(): React.JSX.Element {
  const { show } = useToast();
  const [modules, setModules] = useState<AdminModule[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [pending, setPending] = useState<Set<string>>(new Set());
  const [disableTarget, setDisableTarget] = useState<AdminModule | null>(null);

  const reload = useCallback(() => {
    setLoadError(null);
    fetchModules()
      .then(setModules)
      .catch((err: unknown) => setLoadError(errorMessage(err, "We couldn't load the modules.")))
      .finally(() => setLoading(false));
  }, []);

  useEffect(reload, [reload]);

  const apply = useCallback(
    async (mod: AdminModule, enabled: boolean): Promise<void> => {
      setPending((prev) => new Set(prev).add(mod.id));
      try {
        await setModuleEnabled(mod.id, enabled);
        setModules((prev) => prev.map((m) => (m.id === mod.id ? { ...m, enabled } : m)));
        show({
          title: enabled ? `${mod.id} enabled` : `${mod.id} disabled`,
          text: "Each user's menu updates the next time their page loads.",
          tone: "success",
        });
        setDisableTarget(null);
      } catch (err) {
        show({ title: "We couldn't change the module's status", text: errorMessage(err, "Please try again."), tone: "danger" });
      } finally {
        setPending((prev) => {
          const next = new Set(prev);
          next.delete(mod.id);
          return next;
        });
      }
    },
    [show],
  );

  const columns = useMemo<Column<AdminModule>[]>(
    () => [
      {
        key: "id",
        label: "Module",
        minWidth: 240,
        render: (m) => (
          <div className="nb-cell-stack">
            <strong className="nb-mono" style={{ fontSize: 13 }}>
              {m.id}
            </strong>
            <span className="nb-cell-sub">{m.parentId ? `submodule of ${m.parentId}` : "main module"}</span>
          </div>
        ),
      },
      { key: "version", label: "Version", type: "mono", width: 120 },
      {
        key: "permissions",
        label: "Permissions",
        type: "number",
        width: 112,
        value: (m) => m.permissions.length,
        text: (m) => fmt.number(m.permissions.length),
      },
      {
        key: "enabled",
        label: "Status",
        type: "badge",
        width: 150,
        badge: (m) => (m.enabled ? { label: "Enabled", tone: "success" } : { label: "Disabled", tone: "neutral" }),
        filter: {
          options: [
            { value: "true", label: "Enabled" },
            { value: "false", label: "Disabled" },
          ],
        },
      },
      {
        key: "toggle",
        label: "Enable",
        width: 104,
        align: "right",
        sortable: false,
        csv: false,
        render: (m) => (
          <Switch
            checked={m.enabled}
            disabled={pending.has(m.id)}
            label={m.enabled ? `Disable ${m.id}` : `Enable ${m.id}`}
            onChange={(on) => (on ? void apply(m, true) : setDisableTarget(m))}
          />
        ),
      },
    ],
    [pending, apply],
  );

  return (
    <div>
      <PageHead titleHidden title="Modules" />
      <DataTable<AdminModule>
        id="admin-modules"
        rowKey="id"
        loading={loading}
        rows={modules}
        columns={columns}
        searchPlaceholder="Search modules"
        renderExpanded={(m) =>
          m.permissions.length === 0 ? (
            <p className="nb-help">This module doesn't declare any permissions.</p>
          ) : (
            <dl className="nb-kv">
              {m.permissions.map((p) => (
                <div key={p.key}>
                  <dt>{p.key}</dt>
                  <dd style={{ fontWeight: 400 }}>{p.description}</dd>
                </div>
              ))}
            </dl>
          )
        }
        error={loadError ? { title: "We couldn't load the modules", description: loadError, action: <Button label="Try again" variant="secondary" icon="refresh" onClick={reload} /> } : null}
        empty={{ icon: "layers", title: "No modules installed", description: "Modules are installed with the backend and show up here automatically." }}
      />

      <ConfirmDialog
        open={disableTarget !== null}
        tone="warning"
        icon="power"
        title="Disable module"
        description={disableTarget ? `${disableTarget.id} will no longer be available to any user. Its data is kept and you can enable it again.` : undefined}
        confirmLabel="Disable"
        onConfirm={() => (disableTarget ? apply(disableTarget, false) : undefined)}
        onClose={() => setDisableTarget(null)}
      />
    </div>
  );
}
